// RolePicker — choose a role by typing part of its name (Shohini, 29 Oct
// 2026: the long native list on Assessment approvals was hard to use; type
// "PGT Maths" and pick it). One component for every place the console asks
// for a role: Assessment approvals, Campaigns, the round announcement.
//
// On the EquaOS design system's tokens, drawn like its Select. Keyboard: type
// to narrow the list, Up/Down to move, Enter to pick, Escape to close. It is
// an ARIA combobox with a listbox, and its label is a real <label>.
//
//   roles      [{ id, title }]
//   value      the chosen id ('' or the allOption's value for none)
//   onChange   (id) => …
//   allOption  optional { value, label } — e.g. { value: 'all', label: 'Every role' }
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';

/* Short forms schools type for subjects. */
const ALIASES = {
  maths: 'mathematics', math: 'mathematics', sci: 'science', eng: 'english', bio: 'biology',
  chem: 'chemistry', phy: 'physics', phys: 'physics', cs: 'computer', comp: 'computer', evs: 'environmental',
  pe: 'physical', soc: 'social', sst: 'social', hin: 'hindi', econ: 'economics', acc: 'accountancy',
};
const words = (s) => String(s || '').toLowerCase().normalize('NFKD').split(/[^a-z0-9]+/).filter(Boolean);

/** Does a role's title match what was typed? Every typed word must start one
    of the title's words (so "PGT Maths" finds "PGT – Mathematics"). */
export function roleMatches(title, query) {
  const want = words(query);
  if (!want.length) return true;
  const have = words(title);
  return want.every((q) => {
    const forms = [q, ALIASES[q], q.length > 3 && q.endsWith('s') ? q.slice(0, -1) : null].filter(Boolean);
    return have.some((h) => forms.some((f) => h.startsWith(f)));
  });
}

export default function RolePicker({
  label = 'Role', roles = [], value = '', onChange, allOption = null,
  placeholder = 'Type to find a role…', disabled = false, hint, id,
}) {
  const auto = useId();
  const inputId = id || `role-picker-${auto}`;
  const listId = `${inputId}-list`;
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);

  const options = useMemo(() => [
    ...(allOption ? [{ id: allOption.value, title: allOption.label, all: true }] : []),
    ...roles.map((r) => ({ id: String(r.id ?? r._id), title: r.title || '(untitled role)' })),
  ], [roles, allOption]);
  const chosen = options.find((o) => o.id === String(value ?? ''));
  const shown = useMemo(() => options.filter((o) => o.all ? !query : roleMatches(o.title, query)), [options, query]);

  useEffect(() => { setActive(0); }, [query, open]);
  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpen(false); setQuery(''); } };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const pick = (o) => {
    if (onChange) onChange(o.id);
    setOpen(false);
    setQuery('');
  };
  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); if (!open) setOpen(true); else setActive((i) => Math.min(i + 1, shown.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter') { if (open && shown[active]) { e.preventDefault(); pick(shown[active]); } }
    else if (e.key === 'Escape') { setOpen(false); setQuery(''); }
  };
  const optionId = (i) => `${listId}-${i}`;

  return (
    <div ref={ref} style={{ display: 'grid', gap: '6px', position: 'relative' }}>
      {label && <label htmlFor={inputId} style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)' }}>{label}</label>}
      <input
        id={inputId}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && shown[active] ? optionId(active) : undefined}
        autoComplete="off"
        disabled={disabled}
        value={open ? query : (chosen ? chosen.title : '')}
        placeholder={chosen && open ? chosen.title : placeholder}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onKeyDown={onKeyDown}
        style={{
          height: '40px', padding: '0 14px', borderRadius: 'var(--radius-md)',
          border: `1px solid ${open ? 'var(--focus-ring-color)' : 'var(--border-strong)'}`,
          boxShadow: open ? 'var(--focus-ring-shadow)' : 'none', outline: 'none',
          background: disabled ? 'var(--surface-2)' : 'var(--surface-1)', color: 'var(--text-1)',
          font: 'inherit', fontSize: 'var(--fs-body-md)', width: '100%',
        }}
      />
      {open && (
        <ul id={listId} role="listbox" aria-label={label || 'Roles'} style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0, zIndex: 30, listStyle: 'none', margin: 0,
          background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-lg)', padding: '6px', maxHeight: '260px', overflowY: 'auto',
        }}>
          {shown.length === 0 && (
            <li style={{ padding: '9px 12px', fontSize: 'var(--fs-body-sm)', color: 'var(--text-muted)' }}>No role matches “{query}”.</li>
          )}
          {shown.map((o, i) => {
            const selected = chosen?.id === o.id;
            return (
              <li
                key={o.id || 'all'}
                id={optionId(i)}
                role="option"
                aria-selected={selected}
                onMouseDown={(e) => { e.preventDefault(); pick(o); }}
                onMouseEnter={() => setActive(i)}
                style={{
                  padding: '9px 12px', borderRadius: 'var(--radius-sm)', fontSize: 'var(--fs-body-md)', cursor: 'pointer',
                  background: i === active ? 'var(--surface-2)' : selected ? 'var(--brand-primary-soft)' : 'transparent',
                  color: selected ? 'var(--brand-primary-ink)' : 'var(--text-1)',
                }}
              >{o.title}</li>
            );
          })}
        </ul>
      )}
      {hint && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>{hint}</span>}
    </div>
  );
}
